import React, { useState } from "react";
import { _axios } from "../../utils/axios";
import { uploadFileToBlob } from "../../utils/azure-storage-blob";
import { ErrorToast, SuccessToast } from "../toastMessage";
import { DOCUMENT_CONFIG_ID } from "../../config/phaseConstants";

/**
 * One row of the admin-managed "Permanent RL Continuation Document" list
 * (Tentative RL Copy, Sale Deed, and anything else added under that same
 * document_Type later), as returned by GET
 * /api/document-config/permanent-rl-continuation.
 */
type TContinuationDocumentConfig = {
  document_config_id: number;
  document_label: string;
  mandatory: 0 | 1;
  active: 0 | 1;
};

/**
 * The applicant's signed Application Form is asked for here in the place the
 * K Prat Document used to have (CS1-I88). It is an ordinary main-form document
 * rather than a "Permanent RL Continuation Document" row, so the continuation
 * endpoint never returns it - it is listed here and then travels through the
 * same upload / validation / `documents` payload path as those rows. The copy
 * uploaded with the Tentative RL application is shown as already uploaded.
 */
const APPLICATION_FORM_DOCUMENT_CONFIG: TContinuationDocumentConfig = {
  document_config_id: DOCUMENT_CONFIG_ID.APPLICATION_FORM,
  document_label: "Application Form",
  mandatory: 1,
  active: 1,
};

interface PermanentRLUploadModalProps {
  applicationId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * No longer read: the modal stopped asking for K Prat (CS1-I88). Kept
   * optional so existing callers still compile.
   */
  existingKPratUrl?: string;
  existingRegistryCertUrl?: string;
  /**
   * The application's full generic document map (application.documents,
   * keyed by document_config_id), used to pre-fill already-uploaded
   * Continuation documents. K Prat/Registry Certificate don't need this -
   * they get their saved URL from their own dedicated props above - this
   * exists so any document added purely through the admin panel is
   * pre-filled the same way without a new prop per document.
   */
  /**
   * BEFORE - typed as objects only:
   * existingDocuments?: Record<string, { document?: string } | undefined>;
   *
   * The applicant's application response gives each entry as the url string
   * itself; the reviewer responses give an object carrying `document`.
   */
  existingDocuments?: Record<string, string | { document?: string } | undefined>;
}

export const PermanentRLUploadModal: React.FC<PermanentRLUploadModalProps> = ({
  applicationId,
  isOpen,
  onClose,
  onSuccess,
  /**
   * BEFORE - the saved K Prat url was read here to pre-fill its slot:
   * existingKPratUrl,
   *
   * The slot no longer exists, so the prop is left unread.
   */
  existingRegistryCertUrl,
  existingDocuments,
}) => {
  /**
   * BEFORE - K Prat had its own file state:
   * const [kPratFile, setKPratFile] = useState<File | null>(null);
   */
  const [registryCertFile, setRegistryCertFile] = useState<File | null>(null);
  /**
   * BEFORE - K Prat had its own saved-url state:
   * const [localKPratUrl, setLocalKPratUrl] = useState<string | undefined>(existingKPratUrl);
   */
  const [localRegistryCertUrl, setLocalRegistryCertUrl] = useState<string | undefined>(existingRegistryCertUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState("");

  /**
   * BEFORE - started empty and held only the admin-configured rows:
   * const [continuationConfigs, setContinuationConfigs] = useState<TContinuationDocumentConfig[]>([]);
   *
   * The Application Form is always first in this list, including before the
   * admin rows load or if that request fails, so it can never be skipped.
   */
  const [continuationConfigs, setContinuationConfigs] = useState<TContinuationDocumentConfig[]>([
    APPLICATION_FORM_DOCUMENT_CONFIG,
  ]);
  const [continuationFiles, setContinuationFiles] = useState<Record<number, File>>({});
  const [continuationSavedUrls, setContinuationSavedUrls] = useState<Record<number, string>>({});

  /**
   * BEFORE - kept the K Prat saved url in step with its prop:
   * React.useEffect(() => {
   *   setLocalKPratUrl(existingKPratUrl);
   * }, [existingKPratUrl]);
   */

  React.useEffect(() => {
    setLocalRegistryCertUrl(existingRegistryCertUrl);
  }, [existingRegistryCertUrl]);

  React.useEffect(() => {
    if (!isOpen) return;
    _axios
      .get("/document-config/permanent-rl-continuation")
      .then((res) => {
        /**
         * BEFORE - only the admin-configured rows:
         * const configs: TContinuationDocumentConfig[] = res.data?.data || [];
         */
        const configs: TContinuationDocumentConfig[] = [
          APPLICATION_FORM_DOCUMENT_CONFIG,
          ...(res.data?.data || []),
        ];
        setContinuationConfigs(configs);
        setContinuationSavedUrls(
          configs.reduce((acc, config) => {
            /**
             * BEFORE - assumed every entry was an object with a `document` url:
             * const savedUrl = existingDocuments?.[String(config.document_config_id)]?.document;
             *
             * The applicant's own application response maps each
             * document_config_id straight to its url string, so that lookup
             * always came back empty and nothing was ever pre-filled. Both
             * shapes are read now. This is what lets the Application Form
             * signed and uploaded with the Tentative RL application show up
             * here as already uploaded, so it does not have to be uploaded a
             * second time (it can still be replaced with "Change File").
             */
            const existingEntry = existingDocuments?.[String(config.document_config_id)];
            const savedUrl = typeof existingEntry === "string" ? existingEntry : existingEntry?.document;
            if (savedUrl) acc[config.document_config_id] = savedUrl;
            return acc;
          }, {} as Record<number, string>)
        );
      })
      .catch((err) => console.error("Failed to load Permanent RL continuation document config:", err));
  }, [isOpen]);

  if (!isOpen) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAction = async (isSubmit: boolean) => {
    /**
     * BEFORE - K Prat and Registry Certificate were both required:
     *
     * // If final submission, ensure both files exist (newly selected, locally saved in draft, or existing from server)
     * const hasKPrat = Boolean(kPratFile || localKPratUrl || existingKPratUrl);
     * const hasRegistry = Boolean(registryCertFile || localRegistryCertUrl || existingRegistryCertUrl);
     *
     * if (isSubmit && (!hasKPrat || !hasRegistry)) {
     *   ErrorToast("Please upload both K Prat and Registry Certificate documents for final submission!");
     *   return;
     * }
     *
     * Final submission now needs the Registry Certificate here (newly
     * selected, saved in draft, or existing from server); the Application
     * Form is checked with the other mandatory documents just below.
     */
    const hasRegistry = Boolean(registryCertFile || localRegistryCertUrl || existingRegistryCertUrl);

    if (isSubmit && !hasRegistry) {
      ErrorToast("Please upload the Registry Certificate document for final submission!");
      return;
    }

    const missingMandatoryConfig = continuationConfigs.find(
      (config) =>
        config.mandatory &&
        !continuationFiles[config.document_config_id] &&
        !continuationSavedUrls[config.document_config_id]
    );
    if (isSubmit && missingMandatoryConfig) {
      ErrorToast(`Please upload ${missingMandatoryConfig.document_label} for final submission!`);
      return;
    }

    /**
     * BEFORE - a newly chosen K Prat file also counted as "something to save":
     * if (!isSubmit && !kPratFile && !registryCertFile && !Object.keys(continuationFiles).length) {
     */
    if (!isSubmit && !registryCertFile && !Object.keys(continuationFiles).length) {
      ErrorToast("Please select at least one document to save draft!");
      return;
    }

    try {
      setIsUploading(true);
      /**
       * BEFORE - K Prat was uploaded first:
       *
       * let uploadedKPratUrl = "";
       *
       * if (kPratFile) {
       *   setUploadStatusText("Uploading K Prat document...");
       *   uploadedKPratUrl = await uploadFileToBlob(
       *     kPratFile,
       *     kPratFile.name,
       *     "permanent_rl",
       *     applicationId
       *   );
       * }
       */
      let uploadedRegistryCertUrl = "";

      if (registryCertFile) {
        setUploadStatusText("Uploading Registry Certificate...");
        uploadedRegistryCertUrl = await uploadFileToBlob(
          registryCertFile,
          registryCertFile.name,
          "permanent_rl",
          applicationId
        );
      }

      const uploadedContinuationUrls: Record<number, string> = {};
      for (const config of continuationConfigs) {
        const file = continuationFiles[config.document_config_id];
        if (!file) continue;
        setUploadStatusText(`Uploading ${config.document_label}...`);
        uploadedContinuationUrls[config.document_config_id] = await uploadFileToBlob(
          file,
          file.name,
          "permanent_rl",
          applicationId
        );
      }

      const documents = continuationConfigs
        .map((config) => ({
          documentConfigId: config.document_config_id,
          url:
            uploadedContinuationUrls[config.document_config_id] ||
            continuationSavedUrls[config.document_config_id],
        }))
        .filter((d) => !!d.url);

      setUploadStatusText(isSubmit ? "Submitting Application..." : "Saving Draft...");
      const response = await _axios.post("/applications/submit-permanent-rl", {
        id: applicationId,
        /**
         * BEFORE - the K Prat url was sent as its own field:
         * kPratUrl: uploadedKPratUrl || localKPratUrl || undefined,
         *
         * The Application Form that replaces it goes inside `documents` below.
         */
        registryCertUrl: uploadedRegistryCertUrl || localRegistryCertUrl || undefined,
        documents,
        isSubmit,
      });

      if (response.data?.success) {
        if (isSubmit) {
          SuccessToast("Permanent RL application submitted successfully!");
          onSuccess();
          onClose();
        } else {
          SuccessToast("Draft saved successfully!");
          /**
           * BEFORE - the K Prat draft state was refreshed alongside Registry Certificate:
           * if (uploadedKPratUrl) setLocalKPratUrl(uploadedKPratUrl);
           * setKPratFile(null);
           *
           * That state is gone; the Application Form draft is kept by the
           * continuation-document state updated just below.
           */
          if (uploadedRegistryCertUrl) setLocalRegistryCertUrl(uploadedRegistryCertUrl);
          setRegistryCertFile(null);
          if (Object.keys(uploadedContinuationUrls).length) {
            setContinuationSavedUrls((prev) => ({ ...prev, ...uploadedContinuationUrls }));
            setContinuationFiles({});
          }
        }
      } else {
        ErrorToast(response.data?.message || "Failed to process request.");
      }
    } catch (err: any) {
      console.error(err);
      ErrorToast(err?.response?.data?.message || "An error occurred while uploading documents.");
    } finally {
      setIsUploading(false);
      setUploadStatusText("");
    }
  };

  const getFileNameFromUrl = (url?: string) => {
    if (!url) return "";
    try {
      const cleanUrl = url.split("?")[0];
      const fileName = cleanUrl.split("/").pop() || "";
      return decodeURIComponent(fileName);
    } catch (e) {
      return "Saved Document";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl border border-gray-200">
        {/* Modal Header */}
        <div className="flex justify-between items-center pb-4 mb-5 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Apply for Permanent RL</h3>
            {/* BEFORE - the header carried this subtitle. It named K Prat, which this
                modal no longer asks for, so the line is not shown any more (CS1-I88):
            <p className="text-xs text-gray-500 mt-1">
              Upload required K Prat & Registry documents to initiate Phase 2 review.
            </p>
            */}
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 font-bold text-2xl w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition"
          >
            &times;
          </button>
        </div>

        {/* Modal Content */}
        <div className="space-y-6">
          {/* BEFORE - File 1 was the K Prat Document. It is no longer asked for here;
              the Application Form (rendered with the list below) replaces it (CS1-I88).
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-bold text-gray-800">
                1. K Prat Document <span className="text-red-500">*</span>
              </label>
              {localKPratUrl && (
                <a
                  href={localKPratUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <i className="fa fa-eye"></i> View Existing
                </a>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-2 shrink-0">
                <i className="fa fa-upload"></i>
                {localKPratUrl || kPratFile ? "Change File" : "Choose K Prat File"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setKPratFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <div className="flex-1 min-w-0">
                {kPratFile ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 New: {kPratFile.name}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                      {formatFileSize(kPratFile.size)}
                    </span>
                  </div>
                ) : localKPratUrl ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-green-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 {getFileNameFromUrl(localKPratUrl)}
                    </span>
                    <span className="text-[10px] font-bold text-green-700 shrink-0">
                      ✓ Saved in Draft
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">No file selected (.pdf, .jpg, .png)</span>
                )}
              </div>
            </div>
          </div>
          */}

          {/* BEFORE - this block was headed "File 2: Registry Certificate". With the
              K Prat slot gone it is now the first document in the modal. */}
          {/* File 1: Registry Certificate */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-bold text-gray-800">
                {/* BEFORE - numbered second, after K Prat:
                2. Registry Certificate <span className="text-red-500">*</span>
                */}
                1. Registry Certificate <span className="text-red-500">*</span>
              </label>
              {localRegistryCertUrl && (
                <a
                  href={localRegistryCertUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <i className="fa fa-eye"></i> View Existing
                </a>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-2 shrink-0">
                <i className="fa fa-upload"></i>
                {localRegistryCertUrl || registryCertFile ? "Change File" : "Choose Registry File"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setRegistryCertFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <div className="flex-1 min-w-0">
                {registryCertFile ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 New: {registryCertFile.name}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                      {formatFileSize(registryCertFile.size)}
                    </span>
                  </div>
                ) : localRegistryCertUrl ? (
                  <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-green-300">
                    <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                      📄 {getFileNameFromUrl(localRegistryCertUrl)}
                    </span>
                    <span className="text-[10px] font-bold text-green-700 shrink-0">
                      ✓ Saved in Draft
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">No file selected (.pdf, .jpg, .png)</span>
                )}
              </div>
            </div>
          </div>

          {/* Admin-configured Continuation documents (Tentative RL Copy, Sale Deed, etc.) -
              rendered from GET /document-config/permanent-rl-continuation instead of
              hardcoded, so a new mandatory document added purely through the admin
              panel shows up here with no code change. */}
          {/* The Application Form is the first entry of this same list (it is put
              there when the list is built), so it renders here ahead of the
              admin-configured rows and is validated exactly like them. */}
          {continuationConfigs.map((config, index) => {
            const file = continuationFiles[config.document_config_id];
            const savedUrl = continuationSavedUrls[config.document_config_id];
            return (
              <div key={config.document_config_id} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-gray-800">
                    {/* BEFORE - numbering started at 3 because two fixed slots (K Prat,
                        Registry Certificate) came first; only Registry Certificate does now:
                    {index + 3}. {config.document_label}{" "}
                    */}
                    {index + 2}. {config.document_label}{" "}
                    {!!config.mandatory && <span className="text-red-500">*</span>}
                  </label>
                  {savedUrl && (
                    <a
                      href={savedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                    >
                      <i className="fa fa-eye"></i> View Existing
                    </a>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <label className="cursor-pointer bg-primary text-white text-xs font-bold px-4 py-2 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-2 shrink-0">
                    <i className="fa fa-upload"></i>
                    {savedUrl || file ? "Change File" : `Choose ${config.document_label} File`}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        setContinuationFiles((prev) => ({
                          ...prev,
                          [config.document_config_id]: e.target.files?.[0] as File,
                        }))
                      }
                      className="hidden"
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    {file ? (
                      <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-300">
                        <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                          📄 New: {file.name}
                        </span>
                        <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                    ) : savedUrl ? (
                      <div className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-green-300">
                        <span className="text-xs font-semibold text-gray-800 truncate mr-2">
                          📄 {getFileNameFromUrl(savedUrl)}
                        </span>
                        <span className="text-[10px] font-bold text-green-700 shrink-0">
                          ✓ Saved in Draft
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No file selected (.pdf, .jpg, .png)</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Upload Status Banner */}
          {isUploading && (
            <div className="bg-gray-100 border border-gray-300 p-3 rounded-lg flex items-center gap-3 text-gray-800 text-xs font-semibold">
              <i className="fa fa-spinner fa-spin text-primary text-sm"></i>
              {uploadStatusText}
            </div>
          )}

          {/* Modal Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="bg-black px-6 py-2 rounded-full font-bold text-white text-xs disabled:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleAction(false)}
              /**
               * BEFORE - a newly chosen K Prat file also enabled Save Draft:
               * disabled={isUploading || (!kPratFile && !registryCertFile && !Object.keys(continuationFiles).length)}
               */
              disabled={isUploading || (!registryCertFile && !Object.keys(continuationFiles).length)}
              /**
               * Same black as Cancel.
               * BEFORE: className="bg-gray-700 text-white px-6 py-2 rounded-full font-bold text-xs hover:bg-gray-800 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
               */
              className="bg-black px-6 py-2 rounded-full font-bold text-white text-xs disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save Draft
            </button>
            <button
              type="button"
              onClick={() => handleAction(true)}
              disabled={
                isUploading ||
                /**
                 * BEFORE - Submit also stayed disabled until K Prat was present:
                 * (!kPratFile && !localKPratUrl) ||
                 *
                 * The Application Form is mandatory in continuationConfigs, so
                 * the check below covers it.
                 */
                (!registryCertFile && !localRegistryCertUrl) ||
                continuationConfigs.some(
                  (config) =>
                    config.mandatory &&
                    !continuationFiles[config.document_config_id] &&
                    !continuationSavedUrls[config.document_config_id]
                )
              }
              className="bg-primary px-6 py-2 rounded-full font-bold text-white text-xs hover:opacity-90 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Submit for Permanent RL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
